import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FlightsClassTypes } from 'constants/flight'
import { charterFareCabinLabelMap } from 'constants/charterFlight'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFlightFareClassModal from './MaldivesCharterFlightFareClassModal'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'

interface Props {
  offer?: App.Offer
  fareClass: FlightsClassTypes
  className?: string
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
}

function CharterFlightFareClassSelect({ offer, fareClass, className, checkInDate, checkOutDate, occupancies }: Props) {
  const intl = useIntl()
  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer?.campaignTags,
      occupancies,
    })
  }, [offer?.campaignTags, occupancies])
  const canOpenModal = charterFareClassPolicy.allowedFareClasses.length > 1

  if (!canOpenModal) {
    return null
  }

  const openFareClassModal = async() => {
    const selectedClass = await openModal<FlightsClassTypes>(<MaldivesCharterFlightFareClassModal
      selectedClassId={fareClass}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={occupancies}
      allowedFareClasses={charterFareClassPolicy.allowedFareClasses}
    />)
    if (selectedClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
    }
  }

  return (
    <OfferFilterDropdownBox
      className={className}
      data-testid="fare-class-select"
      onClick={openFareClassModal}
      icon={<LineSeatIcon />}
      label={intl.formatMessage({
        id: '9AQ9b5pIpw',
        defaultMessage: 'Class',
        description: 'Label for charter flight fare class selector',
      })}
      value={charterFareCabinLabelMap[fareClass]}
      placeholder={intl.formatMessage({
        id: 's+t6Cr/PH8',
        defaultMessage: 'Select fare class',
        description: 'Placeholder text for charter flight fare class selector',
      })}
    />
  )
}

type MappedProps = Pick<Props, 'offer' | 'fareClass' | 'checkInDate' | 'checkOutDate' | 'occupancies'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.offer,
  fareClass: state.fareClass,
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  occupancies: state.rooms,
}), CharterFlightFareClassSelect)
